import React from 'react';
import { fmtPrice, fmtUsd } from '../utils/fmtPrice';
import './SingleAssetHlPositionsPanel.css';

function fmtQty(n, side) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  let body;
  if (abs >= 100) body = abs.toFixed(2);
  else if (abs >= 1) body = abs.toFixed(4).replace(/\.?0+$/, '');
  else body = abs.toFixed(8).replace(/\.?0+$/, '');
  const signed = String(side).toLowerCase() === 'short' ? `-${body}` : body;
  return signed;
}

function fmtPx(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return fmtPrice(Number(n));
}

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return fmtUsd(Number(n));
}

function fmtPnlRoe(pnl, roe) {
  if (pnl == null || !Number.isFinite(Number(pnl))) return { text: '—', cls: '' };
  const p = Number(pnl);
  const pnlText = `${p >= 0 ? '+' : ''}${fmtUsd(p)}`;
  let roeText = '';
  if (roe != null && Number.isFinite(Number(roe))) {
    // HL returnOnEquity is a fraction (e.g. -0.00267 → -0.27%)
    const pct = Number(roe) * 100;
    roeText = ` (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)`;
  }
  return {
    text: `${pnlText}${roeText}`,
    cls: p > 0 ? 'up' : p < 0 ? 'down' : '',
  };
}

function fmtFunding(n) {
  if (n == null || !Number.isFinite(Number(n))) return { text: '—', cls: '' };
  const v = Number(n);
  // HL UI shows funding paid as negative for longs typically
  const text = `${v > 0 ? '+' : ''}${fmtUsd(v)}`;
  return { text, cls: v > 0 ? 'up' : v < 0 ? 'down' : '' };
}

function rowFromPosition(p) {
  const market = p.market || `${p.symbol}-USDC`;
  const size = p.sizeContracts;
  const positionValue = p.positionValue ?? p.sizeUsd ?? p.notionalUsd;
  const margin = p.marginUsed ?? p.marginUsd;
  const pnl = fmtPnlRoe(p.unrealizedPnl, p.returnOnEquity);
  const funding = fmtFunding(p.fundingSinceOpen);
  return {
    key: p.lotKey || `${market}:${p.side}`,
    market,
    side: p.side,
    size,
    positionValue,
    entryPrice: p.entryPrice,
    markPrice: p.markPrice,
    pnl,
    liquidationPx: p.liquidationPx,
    margin,
    funding,
    isPaper: !!p.isPaper,
  };
}

/**
 * Hyperliquid-style Positions strip (Market … Funding).
 * Prefers `positions` from clearinghouse; falls back to `lots`.
 */
export default function SingleAssetHlPositionsPanel({ positions, lots, loading }) {
  const rows = (positions?.length ? positions : lots || []).map(rowFromPosition);

  return (
    <section className="sahl-pos-panel" aria-label="Open positions">
      <div className="sahl-pos-tabs">
        <span className="sahl-pos-tab active">Positions</span>
        {rows.length > 0 && <span className="sahl-pos-count">{rows.length}</span>}
      </div>

      <div className="sahl-pos-table-wrap">
        <table className="sahl-pos-table">
          <thead>
            <tr>
              <th>Market</th>
              <th>Size</th>
              <th>Position Value</th>
              <th>Entry Price</th>
              <th>Mark Price</th>
              <th>PNL (ROE %)</th>
              <th>Liq. Price</th>
              <th>Margin</th>
              <th>Funding</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="sahl-pos-empty">
                  Loading positions…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="sahl-pos-empty">
                  No open positions yet
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span className="sahl-pos-market">{r.market}</span>
                    {r.isPaper && <span className="sahl-pos-paper">paper</span>}
                  </td>
                  <td className={String(r.side).toLowerCase() === 'short' ? 'down' : 'up'}>
                    {fmtQty(r.size, r.side)}
                  </td>
                  <td>{fmtMoney(r.positionValue)}</td>
                  <td>{fmtPx(r.entryPrice)}</td>
                  <td>{fmtPx(r.markPrice)}</td>
                  <td className={r.pnl.cls}>{r.pnl.text}</td>
                  <td>{fmtPx(r.liquidationPx)}</td>
                  <td>{fmtMoney(r.margin)}</td>
                  <td className={r.funding.cls}>{r.funding.text}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
